// Every daily-run project's next actions, for My actions, and the sidebar's count of the
// viewer's overdue ones. Mirrors lambda_handlers/api/stateful_agents/portfolio/
// {dashboard_actions,dashboard_actions_overdue}.py. See docs/Y2026-Q4/design-my-actions.md.
import { useEffect, useRef } from "react";
import { useQuery } from "react-query";

import { QK } from "../queryKeys";
import { useAgentsHttp } from "../useAgentsHttp";
import { APPLYING_POLL_MS } from "./useDashboard";
import { type ActionListsData } from "./useProjectAgentStatus";

/** One project's lists, as its own All actions tab reads them. Closed actions only while
 * recent, or while an open action's wait names them. */
export type ProjectActions = Omit<
  ActionListsData,
  "team" | "category_names" | "lists_today"
> & {
  project_id: string;
  name: string;
};

export interface DashboardActions {
  /** The day statuses were derived for. */
  today: string;
  /** ``{email: name}`` for owners. */
  team: Record<string, string>;
  category_names: Record<string, string>;
  /** In the dashboard's planned order. */
  projects: ProjectActions[];
}

// Under the project lists' key, so every change that reloads a project's actions
// reloads these too.
const QK_DASHBOARD_ACTIONS = [QK.projectAgentStatus, "dashboard-actions"];

/** ``applying``: whether an agent is taking in a change on any of the projects (the
 * dashboard's ``applying_review``). A push or resolve wakes the owning agent, which may
 * rewrite its actions a minute later, so the lists reload every few seconds while it works
 * and once more as it finishes, as a project's own page does; otherwise the page falls
 * behind and the next change on those lists is refused as stale. */
export const useDashboardActions = (applying: boolean) => {
  const http = useAgentsHttp();
  const query = useQuery<DashboardActions>(
    QK_DASHBOARD_ACTIONS,
    () => http<DashboardActions>("/dashboard/actions", {}),
    { refetchInterval: applying ? APPLYING_POLL_MS : false },
  );
  // The agent's last write can land after the last poll, so read once more when it is done.
  const wasApplying = useRef(applying);
  const { refetch } = query;
  useEffect(() => {
    if (wasApplying.current && !applying) void refetch();
    wasApplying.current = applying;
  }, [applying, refetch]);
  return query;
};

/** How many open actions on ``email`` are overdue, across the daily-run projects. */
export const useOverdueOnMe = (email: string, enabled: boolean) => {
  const http = useAgentsHttp();
  return useQuery<{ overdue: number }>(
    [...QK_DASHBOARD_ACTIONS, "overdue", email],
    () =>
      http<{ overdue: number }>("/dashboard/actions/overdue", {
        actor_email: email,
      }),
    { enabled: enabled && !!email },
  );
};
